import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { adminAPI } from '@/services/api';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { 
    Truck, 
    Search, 
    Filter,
    Download,
    RefreshCw,
    User,
    Building,
    Mail,
    Phone,
    Calendar,
    Star,
    CheckCircle,
    XCircle,
    AlertCircle,
    Package,
    Coins,
    FileText,
    Eye,
    Edit,
    Trash2,
    ChevronRight,
    Loader2,
    ArrowLeft,
    Menu
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { adminSidebarLinks } from '@/config/adminSidebarLinks';
import { toast } from '@/components/ui/use-toast';
import VehicleManagement from '@/components/admin/transporters/VehicleManagement';
import DriverManagement from '@/components/admin/transporters/DriverManagement';
import OfferManagement from '@/components/admin/transporters/OfferManagement';
import BookingManagement from '@/components/admin/transporters/BookingManagement';
import PaymentManagement from '@/components/admin/transporters/PaymentManagement';
import RatingManagement from '@/components/admin/transporters/RatingManagement';
import LogiConnectIdBadge from '@/components/ui/logiconnect-id-badge';

const AdminTransporters = () => {
    const navigate = useNavigate();
    const [transporters, setTransporters] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedTransporter, setSelectedTransporter] = useState(null);
    const [transporterDetails, setTransporterDetails] = useState(null);
    const [transporterDetailsLoading, setTransporterDetailsLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('details');
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [verificationFilter, setVerificationFilter] = useState('all');
    const [deleteDialog, setDeleteDialog] = useState({ open: false, transporter: null });
    const [showMobileList, setShowMobileList] = useState(true); // Pour mobile : true = liste, false = détails

    // Fonction helper pour obtenir l'ID du profil transporteur
    const getTransporterProfileId = (transporter) => {
        if (!transporter) return null;
        // Essayer plusieurs chemins possibles pour l'ID du profil
        return transporter.profile_data?.id || 
               transporter.profile?.id || 
               transporter.transporter_profile?.id ||
               transporter.id; // Fallback sur l'ID utilisateur si pas de profil
    };

    // Fonctions helper pour accéder aux données utilisateur et profil de manière flexible
    const getUserData = (transporterDetails) => {
        if (!transporterDetails) return null;
        // Essayer plusieurs chemins possibles
        return transporterDetails.user || 
               transporterDetails.user_detail ||
               transporterDetails; // Si transporterDetails est directement l'utilisateur
    };

    const getProfileData = (transporterDetails) => {
        if (!transporterDetails) return null;
        // Essayer plusieurs chemins possibles
        return transporterDetails.profile || 
               transporterDetails.profile_data ||
               transporterDetails.transporter_profile ||
               transporterDetails.profile_detail ||
               {}; // Retourner un objet vide si aucun profil trouvé
    };

    // Charger les transporteurs depuis le backend
    useEffect(() => {
        const fetchTransporters = async () => {
            try {
                setLoading(true);
                // Récupérer tous les transporteurs avec toutes les données nécessaires
                const response = await adminAPI.getTransporters({});
                const transportersData = response.data.results || response.data || [];
                
                // Vérifier que les données sont valides
                if (!Array.isArray(transportersData)) {
                    console.error('Format de données invalide:', response.data);
                    toast({
                        title: "Erreur",
                        description: "Format de données invalide reçu du serveur.",
                        variant: "destructive"
                    });
                    return;
                }

                setTransporters(transportersData);
                
                // Sélectionner le premier transporteur par défaut si disponible
                if (transportersData.length > 0) {
                    const firstTransporter = transportersData[0];
                    // Vérifier que le transporteur a bien un ID de profil
                    const profileId = getTransporterProfileId(firstTransporter);
                    if (profileId) {
                        setSelectedTransporter(firstTransporter);
                    } else {
                        console.warn('Le premier transporteur n\'a pas d\'ID de profil valide:', firstTransporter);
                    }
                }
            } catch (error) {
                console.error('Erreur lors du chargement des transporteurs:', error);
                toast({
                    title: "Erreur",
                    description: error.response?.data?.detail || "Impossible de charger les transporteurs.",
                    variant: "destructive"
                });
            } finally {
                setLoading(false);
            }
        };

        fetchTransporters();
    }, []);

    // Charger les détails du transporteur sélectionné
    useEffect(() => {
        const fetchTransporterDetails = async () => {
            if (!selectedTransporter) {
                setTransporterDetails(null);
                return;
            }

            try {
                setTransporterDetailsLoading(true);
                const profileId = getTransporterProfileId(selectedTransporter);
                
                if (!profileId) {
                    console.error('Impossible de trouver l\'ID du profil transporteur:', selectedTransporter);
                    toast({
                        title: "Erreur",
                        description: "Impossible d'identifier le transporteur.",
                        variant: "destructive"
                    });
                    return;
                }

                const response = await adminAPI.getTransporterById(profileId);
                
                // Vérifier que la réponse contient bien les données
                if (!response.data) {
                    console.error('Réponse API invalide:', response);
                    toast({
                        title: "Erreur",
                        description: "Les données reçues sont invalides.",
                        variant: "destructive"
                    });
                    return;
                }

                // Debug: afficher la structure des données
                console.log('=== STRUCTURE DES DONNÉES TRANSPORTEUR ===');
                console.log('Réponse complète:', response.data);
                console.log('user:', response.data.user);
                console.log('profile:', response.data.profile);
                console.log('profile_data:', response.data.profile_data);
                console.log('transporter_profile:', response.data.transporter_profile);
                console.log('Clés principales:', Object.keys(response.data));
                console.log('==========================================');

                setTransporterDetails(response.data);
            } catch (error) {
                console.error('Erreur lors du chargement des détails:', error);
                console.error('Transporteur sélectionné:', selectedTransporter);
                toast({
                    title: "Erreur",
                    description: error.response?.data?.detail || "Impossible de charger les détails du transporteur.",
                    variant: "destructive"
                });
            } finally {
                setTransporterDetailsLoading(false);
            }
        };

        fetchTransporterDetails();
    }, [selectedTransporter]);

    // Filtrage des transporteurs
    const filteredTransporters = transporters.filter(transporter => {
        // Accéder aux données de manière flexible
        const user = transporter.user || transporter.user_detail || transporter;
        const profile = transporter.profile_data || transporter.profile || transporter.transporter_profile || {};
        
        const matchesSearch = 
            user?.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user?.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            profile?.organisation_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            user?.logiconnect_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            profile?.logiconnect_id?.toLowerCase().includes(searchTerm.toLowerCase());
        
        const matchesStatus = statusFilter === 'all' || user?.status === statusFilter;
        const matchesVerification = verificationFilter === 'all' || profile?.verification_status === verificationFilter;
        
        return matchesSearch && matchesStatus && matchesVerification;
    });

    const handleSelectTransporter = (transporter) => {
        setSelectedTransporter(transporter);
        setActiveTab('details');
        // Sur mobile, masquer la liste et afficher les détails
        setShowMobileList(false);
    };

    const handleBackToList = () => {
        setShowMobileList(true);
    };

    const handleDelete = (transporter) => {
        setDeleteDialog({ open: true, transporter });
    };

    const confirmDelete = async () => {
        if (deleteDialog.transporter) {
            try {
                await adminAPI.deleteTransporter(deleteDialog.transporter.id);
                setTransporters(transporters.filter(t => t.id !== deleteDialog.transporter.id));
                if (selectedTransporter?.id === deleteDialog.transporter.id) {
                    setSelectedTransporter(null);
                    setTransporterDetails(null);
                }
                toast({ 
                    title: "Succès", 
                    description: "Transporteur supprimé avec succès.",
                });
            } catch (error) {
                console.error('Erreur lors de la suppression:', error);
                let errorMessage = 'Impossible de supprimer le transporteur';
                if (error.response?.data?.detail) {
                        errorMessage = error.response.data.detail;
                }
                toast({
                    title: "Erreur",
                    description: errorMessage,
                    variant: "destructive"
                });
            }
        }
        setDeleteDialog({ open: false, transporter: null });
    };

    const handleRefresh = async () => {
        try {
            setLoading(true);
            const response = await adminAPI.getTransporters({});
            const transportersData = response.data.results || response.data || [];
            
            // Vérifier que les données sont valides
            if (!Array.isArray(transportersData)) {
                console.error('Format de données invalide:', response.data);
                toast({
                    title: "Erreur",
                    description: "Format de données invalide reçu du serveur.",
                    variant: "destructive"
                });
                return;
            }

            setTransporters(transportersData);
            
            // Si un transporteur est sélectionné, vérifier qu'il existe toujours dans la nouvelle liste
            if (selectedTransporter) {
                const stillExists = transportersData.find(t => t.id === selectedTransporter.id);
                if (!stillExists) {
                    // Le transporteur sélectionné n'existe plus, sélectionner le premier
                    if (transportersData.length > 0) {
                        const firstTransporter = transportersData[0];
                        const profileId = getTransporterProfileId(firstTransporter);
                        if (profileId) {
                            setSelectedTransporter(firstTransporter);
                        } else {
                            setSelectedTransporter(null);
                        }
                    } else {
                        setSelectedTransporter(null);
                    }
                }
            }
            
            toast({ title: "Actualisé", description: "Liste des transporteurs actualisée." });
        } catch (error) {
            console.error('Erreur lors du rafraîchissement:', error);
            toast({
                title: "Erreur",
                description: error.response?.data?.detail || "Impossible d'actualiser la liste.",
                variant: "destructive"
            });
        } finally {
            setLoading(false);
        }
    };

    const getStatusBadge = (status) => {
        const statusMap = {
            'ACTIVE': 'bg-green-500/20 text-green-400 border-green-500/30',
            'PENDING': 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
            'SUSPENDED': 'bg-red-500/20 text-red-400 border-red-500/30',
            'BANNED': 'bg-red-500/20 text-red-400 border-red-500/30',
        };
        const textMap = {
            'ACTIVE': 'Actif',
            'PENDING': 'En attente',
            'SUSPENDED': 'Suspendu',
            'BANNED': 'Banni',
        };
        return <Badge className={`text-xs ${statusMap[status] || 'bg-gray-500/20 text-gray-400 border-gray-500/30'}`}>{textMap[status] || status}</Badge>;
    };

    const getVerificationBadge = (status) => {
        const statusMap = {
            'VERIFIED': 'bg-green-500/20 text-green-400 border-green-500/30',
            'PENDING': 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
            'REJECTED': 'bg-red-500/20 text-red-400 border-red-500/30',
            'UNVERIFIED': 'bg-gray-500/20 text-gray-400 border-gray-500/30',
        };
        const textMap = {
            'VERIFIED': 'Vérifié',
            'PENDING': 'En attente',
            'REJECTED': 'Rejeté',
            'UNVERIFIED': 'Non vérifié',
        };
        return <Badge className={`text-xs ${statusMap[status] || 'bg-gray-500/20 text-gray-400 border-gray-500/30'}`}>{textMap[status] || status}</Badge>;
    };

    const formatCurrency = (amount) => {
        if (!amount && amount !== 0) return '0 GNF';
        return new Intl.NumberFormat('fr-FR').format(amount) + ' GNF';
    };

    return (
        <>
            <Helmet>
                <title>Gestion des Transporteurs - LogiConnect</title>
            </Helmet>
            
            <DashboardLayout sidebarLinks={adminSidebarLinks}>
                <div className="flex h-[calc(100vh-4rem)] md:h-[calc(100vh-4rem)] min-h-0 gap-4">
                    {/* Sidebar gauche - Liste des transporteurs */}
                    {/* Sur mobile : afficher uniquement si showMobileList est true */}
                    <div className={`
                        ${showMobileList ? 'flex' : 'hidden md:flex'}
                        w-full md:w-80 
                        border-r border-border bg-card 
                        flex-col
                    `}>
                        {/* Header avec recherche et filtres */}
                        <div className="p-4 border-b border-border space-y-4">
                            <div className="flex items-center justify-between">
                                <h2 className="text-lg font-semibold">Transporteurs</h2>
                            <Button
                                    variant="ghost"
                                size="icon"
                                onClick={handleRefresh}
                                disabled={loading}
                            >
                                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                            </Button>
                    </div>

                            {/* Recherche */}
                                <div className="relative">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground w-4 h-4" />
                                    <Input
                                    placeholder="Rechercher..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className="pl-10"
                                    />
                            </div>
                            
                            {/* Filtres */}
                            <div className="flex gap-2">
                                <Select value={statusFilter} onValueChange={setStatusFilter}>
                                    <SelectTrigger className="flex-1">
                                        <SelectValue placeholder="Statut" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">Tous</SelectItem>
                                        <SelectItem value="ACTIVE">Actif</SelectItem>
                                        <SelectItem value="PENDING">En attente</SelectItem>
                                        <SelectItem value="SUSPENDED">Suspendu</SelectItem>
                                    </SelectContent>
                                </Select>
                                
                                <Select value={verificationFilter} onValueChange={setVerificationFilter}>
                                    <SelectTrigger className="flex-1">
                                        <SelectValue placeholder="Vérif." />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">Tous</SelectItem>
                                        <SelectItem value="VERIFIED">Vérifié</SelectItem>
                                        <SelectItem value="PENDING">En attente</SelectItem>
                                        <SelectItem value="UNVERIFIED">Non vérifié</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        {/* Liste des transporteurs */}
                        <div className="flex-1 overflow-y-auto">
                            {loading ? (
                                <div className="flex items-center justify-center py-12">
                                    <Loader2 className="w-6 h-6 animate-spin text-primary" />
                                </div>
                            ) : filteredTransporters.length === 0 ? (
                                <div className="p-4 text-center text-muted-foreground text-sm">
                                    Aucun transporteur trouvé
                                </div>
                            ) : (
                                <div className="p-2 space-y-1">
                                    {filteredTransporters.map((transporter) => {
                                        // Accéder aux données de manière flexible
                                        const user = transporter.user || transporter.user_detail || transporter;
                                        const profile = transporter.profile_data || transporter.profile || transporter.transporter_profile || {};
                                        const isSelected = selectedTransporter?.id === transporter.id;
                                        
                                        return (
                                            <div
                                                key={transporter.id}
                                                onClick={() => handleSelectTransporter(transporter)}
                                                className={`
                                                    p-3 rounded-lg cursor-pointer transition-all
                                                    ${isSelected 
                                                        ? 'bg-primary/10 border-2 border-primary' 
                                                        : 'bg-card border border-border hover:bg-muted/50'
                                                    }
                                                `}
                                            >
                                                <div className="flex items-center gap-3">
                                                    <Avatar className="h-10 w-10">
                                                        <AvatarImage 
                                                            src={user?.photo || `https://api.dicebear.com/7.x/initials/svg?seed=${user?.first_name}+${user?.last_name}`} 
                                                            alt={`${user?.first_name} ${user?.last_name}`} 
                                                        />
                                                        <AvatarFallback className="text-xs">
                                                            {user?.first_name?.[0] || 'U'}{user?.last_name?.[0] || 'U'}
                                                        </AvatarFallback>
                                                    </Avatar>
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex items-center gap-2 mb-1">
                                                            <p className="font-semibold text-sm truncate">
                                                                {profile?.organisation_name || `${user?.first_name || ''} ${user?.last_name || ''}`.trim() || 'Transporteur'}
                                                            </p>
                                                            {isSelected && (
                                                                <ChevronRight className="w-4 h-4 text-primary flex-shrink-0" />
                                                            )}
                                                        </div>
                                                        <div className="flex items-center gap-2 flex-wrap">
                                                            {getStatusBadge(user?.status)}
                                                            {getVerificationBadge(profile?.verification_status)}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Side droite - Détails du transporteur sélectionné */}
                    {/* Sur mobile : afficher uniquement si showMobileList est false */}
                    <div className={`
                        ${showMobileList ? 'hidden' : 'flex'}
                        md:flex
                        flex-1 flex flex-col overflow-y-auto
                    `}>
                        {!selectedTransporter ? (
                            <div className="flex items-center justify-center h-full text-muted-foreground p-4">
                                <div className="text-center">
                                    <Truck className="w-12 h-12 md:w-16 md:h-16 mx-auto mb-4 opacity-50" />
                                    <p className="text-sm md:text-lg">Sélectionnez un transporteur pour voir ses détails</p>
                                </div>
                            </div>
                        ) : transporterDetailsLoading ? (
                            <div className="flex items-center justify-center h-full">
                                <Loader2 className="w-8 h-8 animate-spin text-primary" />
                            </div>
                        ) : transporterDetails ? (
                            <>
                                {/* En-tête avec informations du transporteur */}
                                <div className="p-4 md:p-6 border-b border-border bg-card">
                                    {/* Bouton retour sur mobile */}
                                    <div className="md:hidden mb-4">
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={handleBackToList}
                                            className="mb-2"
                                        >
                                            <ArrowLeft className="w-4 h-4 mr-2" />
                                            Retour à la liste
                                        </Button>
                                    </div>
                                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4">
                                        <div className="flex items-center gap-3 md:gap-4 min-w-0 flex-1">
                                            {(() => {
                                                const user = getUserData(transporterDetails);
                                                const profile = getProfileData(transporterDetails);
                                                return (
                                                    <>
                                                        <Avatar className="w-12 h-12 md:w-16 md:h-16 flex-shrink-0">
                                                            <AvatarImage 
                                                                src={user?.photo || `https://api.dicebear.com/7.x/initials/svg?seed=${user?.first_name}+${user?.last_name}`} 
                                                                alt={`${user?.first_name} ${user?.last_name}`} 
                                                            />
                                                            <AvatarFallback>
                                                                {user?.first_name?.[0] || 'U'}{user?.last_name?.[0] || 'U'}
                                                            </AvatarFallback>
                                                        </Avatar>
                                                        <div className="min-w-0 flex-1">
                                                            <div className="flex flex-wrap items-center gap-2 md:gap-3 mb-2">
                                                                <h1 className="text-lg md:text-2xl font-bold break-words">
                                                                    {profile?.organisation_name || 
                                                                     `${user?.first_name || ''} ${user?.last_name || ''}`.trim() || 'Transporteur'}
                                                                </h1>
                                                                {getStatusBadge(user?.status)}
                                                                {getVerificationBadge(profile?.verification_status)}
                                                            </div>
                                                            {profile?.organisation_name && (
                                                                <p className="text-muted-foreground">
                                                                    {user?.first_name} {user?.last_name}
                                                                </p>
                                                            )}
                                                        </div>
                                                    </>
                                                );
                                            })()}
                                        </div>
                                        <div className="flex items-center gap-2 w-full sm:w-auto">
                                            <Button variant="outline" size="sm" className="flex-1 sm:flex-initial">
                                                <Edit className="w-4 h-4 mr-2" />
                                                Modifier
                                            </Button>
                                            <Button 
                                                variant="outline" 
                                                size="sm"
                                                onClick={() => handleDelete(selectedTransporter)}
                                                className="text-destructive hover:text-destructive flex-1 sm:flex-initial"
                                            >
                                                <Trash2 className="w-4 h-4 mr-2" />
                                                Supprimer
                                            </Button>
                                        </div>
                                    </div>

                                    {/* Informations rapides */}
                                    {(() => {
                                        const user = getUserData(transporterDetails);
                                        const profile = getProfileData(transporterDetails);
                                        return (
                                            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-4">
                                                <div>
                                                    <Label className="text-xs text-muted-foreground">Email</Label>
                                                    <p className="text-sm font-medium">{user?.email || 'N/A'}</p>
                                                </div>
                                                <div>
                                                    <Label className="text-xs text-muted-foreground">Téléphone</Label>
                                                    <p className="text-sm font-medium">{user?.phone_number || 'N/A'}</p>
                                                </div>
                                                <div>
                                                    <Label className="text-xs text-muted-foreground">Note moyenne</Label>
                                                    <div className="flex items-center gap-1">
                                                        <Star className="w-4 h-4 text-yellow-400" />
                                                        <p className="text-sm font-medium">{profile?.rating_avg || '0'}</p>
                                                    </div>
                                                </div>
                                                <div>
                                                    <Label className="text-xs text-muted-foreground">Solde</Label>
                                                    <p className="text-sm font-bold text-yellow-400">{formatCurrency(profile?.balance || 0)}</p>
                                                </div>
                                            </div>
                                        );
                                    })()}
                                </div>

                                {/* Onglets */}
                                <div className="flex-1">
                                    <Tabs value={activeTab} onValueChange={setActiveTab} className="h-full flex flex-col">
                                        <div className="px-4 md:px-6 pt-4 border-b border-border overflow-x-auto flex-shrink-0">
                                            <TabsList className="w-full justify-start gap-1 h-auto p-1 bg-muted/50">
                                                <TabsTrigger 
                                                    value="details" 
                                                    className="px-4 md:px-6 py-3 text-sm md:text-base font-medium data-[state=active]:bg-yellow-400 data-[state=active]:text-black data-[state=active]:font-semibold"
                                                >
                                                    Détails
                                                </TabsTrigger>
                                                <TabsTrigger 
                                                    value="offers" 
                                                    className="px-4 md:px-6 py-3 text-sm md:text-base font-medium data-[state=active]:bg-yellow-400 data-[state=active]:text-black data-[state=active]:font-semibold"
                                                >
                                                    Offres
                                                </TabsTrigger>
                                                <TabsTrigger 
                                                    value="bookings" 
                                                    className="px-4 md:px-6 py-3 text-sm md:text-base font-medium data-[state=active]:bg-yellow-400 data-[state=active]:text-black data-[state=active]:font-semibold"
                                                >
                                                    Missions
                                                </TabsTrigger>
                                                <TabsTrigger 
                                                    value="vehicles" 
                                                    className="px-4 md:px-6 py-3 text-sm md:text-base font-medium data-[state=active]:bg-yellow-400 data-[state=active]:text-black data-[state=active]:font-semibold"
                                                >
                                                    Véhicules
                                                </TabsTrigger>
                                                <TabsTrigger 
                                                    value="drivers" 
                                                    className="px-4 md:px-6 py-3 text-sm md:text-base font-medium data-[state=active]:bg-yellow-400 data-[state=active]:text-black data-[state=active]:font-semibold"
                                                >
                                                    Chauffeurs
                                                </TabsTrigger>
                                                <TabsTrigger 
                                                    value="payments" 
                                                    className="px-4 md:px-6 py-3 text-sm md:text-base font-medium data-[state=active]:bg-yellow-400 data-[state=active]:text-black data-[state=active]:font-semibold"
                                                >
                                                    Paiements
                                                </TabsTrigger>
                                                <TabsTrigger 
                                                    value="ratings" 
                                                    className="px-4 md:px-6 py-3 text-sm md:text-base font-medium data-[state=active]:bg-yellow-400 data-[state=active]:text-black data-[state=active]:font-semibold"
                                                >
                                                    Avis
                                                </TabsTrigger>
                                            </TabsList>
                                        </div>

                                        <div className="flex-1 p-4 md:p-6">
                                            <TabsContent value="details" className="mt-0">
                                                {(() => {
                                                    const user = getUserData(transporterDetails);
                                                    const profile = getProfileData(transporterDetails);
                                                    return (
                                                        <div className="space-y-6">
                                                            {/* Informations générales */}
                                                            <Card>
                                                                <CardContent className="p-6">
                                                                    <h3 className="text-lg font-semibold mb-4">Informations Générales</h3>
                                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                                        <div>
                                                                            <Label className="text-muted-foreground text-sm">Type de compte</Label>
                                                                            <p className="font-medium">{profile?.is_company ? 'Entreprise' : 'Particulier'}</p>
                                                                        </div>
                                                                        <div>
                                                                            <Label className="text-muted-foreground text-sm">Email</Label>
                                                                            <p className="font-medium">{user?.email || 'N/A'}</p>
                                                                        </div>
                                                                        <div>
                                                                            <Label className="text-muted-foreground text-sm">Téléphone</Label>
                                                                            <p className="font-medium">{user?.phone_number || 'N/A'}</p>
                                                                        </div>
                                                                        <div>
                                                                            <Label className="text-muted-foreground text-sm">Membre depuis</Label>
                                                                            <p className="font-medium">
                                                                                {user?.date_joined 
                                                                                    ? new Date(user.date_joined).toLocaleDateString('fr-FR')
                                                                                    : 'N/A'}
                                                                            </p>
                                                                        </div>
                                                                        {profile?.registration_number && (
                                                                            <div>
                                                                                <Label className="text-muted-foreground text-sm">N° Enregistrement</Label>
                                                                                <p className="font-medium">{profile.registration_number}</p>
                                                                            </div>
                                                                        )}
                                                                        {profile?.tax_info && (
                                                                            <div>
                                                                                <Label className="text-muted-foreground text-sm">NIF</Label>
                                                                                <p className="font-medium">{profile.tax_info}</p>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </CardContent>
                                                            </Card>

                                                            {/* Statistiques */}
                                                            <Card>
                                                                <CardContent className="p-6">
                                                                    <h3 className="text-lg font-semibold mb-4">Statistiques</h3>
                                                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                                                        <div>
                                                                            <Label className="text-muted-foreground text-sm">Note moyenne</Label>
                                                                            <div className="flex items-center gap-1 mt-1">
                                                                                <Star className="w-4 h-4 text-yellow-400" />
                                                                                <p className="font-bold text-lg">{profile?.rating_avg || '0'}</p>
                                                                            </div>
                                                                        </div>
                                                                        <div>
                                                                            <Label className="text-muted-foreground text-sm">Missions</Label>
                                                                            <p className="font-bold text-lg">{profile?.trips || 0}</p>
                                                                        </div>
                                                                        <div>
                                                                            <Label className="text-muted-foreground text-sm">Véhicules</Label>
                                                                            <p className="font-bold text-lg">{profile?.vehicles || 0}</p>
                                                                        </div>
                                                                        <div>
                                                                            <Label className="text-muted-foreground text-sm">Solde</Label>
                                                                            <p className="font-bold text-lg text-yellow-400">{formatCurrency(profile?.balance || 0)}</p>
                                                                        </div>
                                                                    </div>
                                                                </CardContent>
                                                            </Card>
                                                        </div>
                                                    );
                                                })()}
                                            </TabsContent>

                                            <TabsContent value="offers" className="mt-0">
                                                {selectedTransporter && getTransporterProfileId(selectedTransporter) && (
                                                    <OfferManagement 
                                                        transporterId={getTransporterProfileId(selectedTransporter)}
                                                        onStatisticsUpdate={() => {}}
                                                    />
                                                )}
                                            </TabsContent>

                                            <TabsContent value="bookings" className="mt-0">
                                                {selectedTransporter && getTransporterProfileId(selectedTransporter) && (
                                                    <BookingManagement 
                                                        transporterId={getTransporterProfileId(selectedTransporter)}
                                                        onStatisticsUpdate={() => {}}
                                                    />
                                                )}
                                            </TabsContent>

                                            <TabsContent value="vehicles" className="mt-0">
                                                {selectedTransporter && getTransporterProfileId(selectedTransporter) && (
                                                    <VehicleManagement 
                                                        transporterId={getTransporterProfileId(selectedTransporter)}
                                                        onStatisticsUpdate={() => {}}
                                                    />
                                                )}
                                            </TabsContent>

                                            <TabsContent value="drivers" className="mt-0">
                                                {selectedTransporter && getTransporterProfileId(selectedTransporter) && (
                                                    <DriverManagement 
                                                        transporterId={getTransporterProfileId(selectedTransporter)}
                                                        onStatisticsUpdate={() => {}}
                                                    />
                                                )}
                                            </TabsContent>

                                            <TabsContent value="payments" className="mt-0">
                                                {selectedTransporter && getTransporterProfileId(selectedTransporter) && (
                                                    <PaymentManagement 
                                                        transporterId={getTransporterProfileId(selectedTransporter)}
                                                    />
                                                )}
                                            </TabsContent>

                                            <TabsContent value="ratings" className="mt-0">
                                                {selectedTransporter && getTransporterProfileId(selectedTransporter) && (
                                                    <RatingManagement 
                                                        transporterId={getTransporterProfileId(selectedTransporter)}
                                                    />
                                                )}
                                            </TabsContent>
                                        </div>
                                    </Tabs>
                                </div>
                            </>
                        ) : null}
                    </div>
                </div>

                {/* Dialog de confirmation de suppression */}
                <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, transporter: null })}>
                    <AlertDialogContent>
                        <AlertDialogHeader>
                            <AlertDialogTitle>Êtes-vous absolument sûr ?</AlertDialogTitle>
                            <AlertDialogDescription>
                                Cette action est irréversible. Le transporteur sera définitivement supprimé 
                                ainsi que toutes ses données associées.
                            </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                            <AlertDialogCancel>Annuler</AlertDialogCancel>
                            <AlertDialogAction 
                                onClick={confirmDelete}
                                className="bg-red-600 hover:bg-red-700"
                            >
                                Supprimer
                            </AlertDialogAction>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>
            </DashboardLayout>
        </>
    );
};

export default AdminTransporters;
